import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { AppShell } from "../components/AppShell";
import { PageHeader } from "../components/PageHeader";
import { Card, ErrorBox, Skeleton } from "../components/ui";
import { useEditableContractor, useEmployeeFormOptions, useSaveContractor, type EmploymentProblem } from "../lib/api";
import { blocking, validateContractor, type Problem } from "../lib/employment";
import { AreaField, PickField, Section, TextField } from "./people/PersonFormFields";

/**
 * Add or edit a contractor (WP-15 write screens, OS-0640).
 *
 * Deliberately a separate screen from the employee editor, and deliberately a shorter one. A contractor has no
 * birth date, no joining date, no manager and no employment type — a firm cannot have any of those — and building
 * one form for both would have meant either asking a firm for its date of birth or making half the employee form
 * conditional. What a contractor has instead is a kind (firm or individual), a trade, a status that decides
 * whether its people may be on site, and the identifiers the assignment register matches people on.
 */

const TEXT = (v: unknown) => (v == null ? "" : String(v));

export default function ContractorForm() {
  const { name } = useParams();
  const isNew = !name;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const existing = useEditableContractor(name);
  const options = useEmployeeFormOptions(undefined);
  const [values, setValues] = useState<Record<string, unknown>>({ contractor_type: "Firm", status: "Active" });
  const [touched, setTouched] = useState(false);
  const [warnings, setWarnings] = useState<EmploymentProblem[]>([]);
  const save = useSaveContractor();

  useEffect(() => {
    if (existing.data) setValues({ ...existing.data.values });
  }, [existing.data]);

  const set = (field: string) => (v: string) => { setTouched(true); setValues((prev) => ({ ...prev, [field]: v })); };
  const problems: Problem[] = useMemo(() => validateContractor(values), [values]);
  const shown = touched ? problems : [];
  const opts = options.data;
  const isFirm = TEXT(values.contractor_type) === "Firm";

  const submit = () => {
    setTouched(true);
    if (blocking(problems).length > 0) return;
    save.mutate({ name, values }, {
      onSuccess: (res) => {
        setWarnings(res.warnings ?? []);
        if (isNew) navigate(`/contractors/${encodeURIComponent(res.name)}`, { replace: true });
      },
    });
  };

  if (!isNew && existing.isLoading) {
    return <AppShell subtitle={t("con.title")}><div className="space-y-3">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-28" />)}</div></AppShell>;
  }
  if (existing.isError) {
    return <AppShell subtitle={t("con.title")}><ErrorBox error={existing.error} onRetry={() => void existing.refetch()} /></AppShell>;
  }

  return (
    <AppShell subtitle={t("con.title")} record={isNew ? t("con.form.addTitle") : (TEXT(values.contractor_name) || name)}>
      <PageHeader title={isNew ? t("con.form.addTitle") : TEXT(values.contractor_name) || (name ?? "")}
        subtitle={isNew ? t("con.form.addHint") : t("con.form.editHint")}
        actions={
          <div className="flex flex-wrap items-center gap-2" data-con-form-actions>
            <Link to={isNew ? "/contractors" : `/contractors/${encodeURIComponent(name!)}`} data-con-cancel
              className="rounded border border-brand-grey-light px-3 py-1.5 text-xs font-semibold hover:border-brand-primary">
              {t("common.cancel")}
            </Link>
            <button type="button" onClick={submit} disabled={save.isPending} data-con-save
              className="rounded bg-brand-primary px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
              {save.isPending ? t("emp.form.saving") : isNew ? t("con.form.create") : t("emp.form.save")}
            </button>
          </div>
        } />

      {save.isError && <div className="mt-4"><ErrorBox error={save.error} /></div>}
      {touched && blocking(problems).length > 0 && (
        <Card className="mt-4 border-l-4 border-status-red bg-red-50/50 px-4 py-2 text-sm" data-con-blocked>
          {t("emp.form.blocked", { n: blocking(problems).length })}
        </Card>
      )}
      {save.isSuccess && blocking(problems).length === 0 && (
        <Card className="mt-4 border-l-4 border-status-green bg-green-50/50 px-4 py-2 text-sm" data-con-saved>
          {t("emp.form.saved")}
          {warnings.length > 0 && (
            <ul className="mt-1 list-disc pl-4 text-[11px] text-status-amber">
              {warnings.map((w) => <li key={w.code} data-con-warning={w.code}>{t(`emp.problem.${w.code}`, (w.params ?? {}) as Record<string, unknown>)}</li>)}
            </ul>
          )}
        </Card>
      )}

      <div className="mt-4 space-y-4">
        <Section title={t("con.form.who")} hint={t("con.form.whoHint")} hook="who">
          <TextField field="contractor_name" label={t("con.f.name")} required problems={shown}
            value={TEXT(values.contractor_name)} onChange={set("contractor_name")} />
          <PickField field="contractor_type" label={t("con.f.type")} required problems={shown}
            value={TEXT(values.contractor_type)} onChange={set("contractor_type")}
            options={(opts?.contractor_types ?? ["Firm", "Individual"]).map((v) => ({ value: v, label: t(`con.type.${v}`, v) }))} />
          <TextField field="trade" label={t("con.f.trade")} problems={shown}
            value={TEXT(values.trade)} onChange={set("trade")} />
          <PickField field="company" label={t("emp.f.company")} problems={shown}
            value={TEXT(values.company)} onChange={set("company")}
            options={(opts?.companies ?? []).map((c) => ({ value: String(c.name), label: String(c.name) }))} />
          <PickField field="status" label={t("emp.f.status")} required problems={shown}
            hint={t("con.form.statusHint")}
            value={TEXT(values.status) || "Active"} onChange={set("status")}
            options={(opts?.contractor_statuses ?? []).map((s) => ({ value: s, label: t(`con.status.${s}`, s) }))} />
          <TextField field="national_id" label={t("con.f.nationalId")} problems={shown}
            hint={isFirm ? undefined : t("con.form.idHint")}
            value={TEXT(values.national_id)} onChange={set("national_id")} />
        </Section>

        <Section title={t("con.form.contact")} hook="contact">
          <TextField field="contact_person" label={t("con.f.contactPerson")} problems={shown}
            required={isFirm} value={TEXT(values.contact_person)} onChange={set("contact_person")} />
          <TextField field="phone" label={t("con.f.phone")} type="tel" problems={shown}
            value={TEXT(values.phone)} onChange={set("phone")} />
          <TextField field="email" label={t("con.f.email")} type="email" problems={shown}
            value={TEXT(values.email)} onChange={set("email")} />
          <AreaField field="notes" label={t("con.f.notes")} problems={shown}
            value={TEXT(values.notes)} onChange={set("notes")} />
        </Section>

        <Card className="border-l-4 border-brand-primary bg-blue-50/40 px-4 py-3 text-sm" data-con-form-note>
          {t("con.form.engagementsElsewhere")}
        </Card>
      </div>
    </AppShell>
  );
}
